import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3,
  Upload,
  FileText,
  LogOut,
  Home,
  Settings,
  User,
  Bell,
  Database,
  Inbox,
  Clock,
  Lock,
  Code,
  Trash2,
  TrendingUp,
  Users,
  FileBox,
  XCircle,
  BookOpen,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./MainLayout.css";

function MainLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const menuItems = [
    // Main Operations
    { path: "/dashboard", icon: Home, label: "Dashboard" },
    { path: "/work-orders", icon: FileText, label: "Work Orders" },
    { path: "/batch-extract", icon: Upload, label: "Batch Orders" },
    // { path: "/bulk-create", icon: Upload, label: "Bulk Create" },
    { path: "/email-work-orders", icon: Inbox, label: "Email Work Orders" },

    // Management
    { path: "/sessions", icon: Clock, label: "Sessions" },
    { path: "/users", icon: Users, label: "Users" },
    { path: "/usage-report", icon: BarChart3, label: "Usage Report" },
    { path: "/analytics", icon: TrendingUp, label: "Analytics" },
    { path: "/database", icon: Database, label: "DB Management" },

    // System
    { path: "/original-templates", icon: FileBox, label: "AMC Templates" },
    { path: "/cancellation-forms", icon: XCircle, label: "Cancellation Forms" },
    { path: "/purge-records", icon: Trash2, label: "Purge Records" },
    { path: "/settings", icon: Settings, label: "Settings" },
    { path: "/api-logs", icon: Code, label: "API Logs" },
    { path: "/portal-logs", icon: Lock, label: "Portal Logs" },
  ];

  // Get API base URL for Swagger UI
  const getApiDocsUrl = () => {
    // Development: Flask runs on port 5001
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:5001/api-docs';
    }
    // Production: API is on same domain
    return `${window.location.protocol}//${window.location.hostname}/api-docs`;
  };

  // Add API Docs to menu items
  menuItems.push({
    path: getApiDocsUrl(),
    icon: BookOpen,
    label: "API Docs",
    external: true
  });

  // Pipeline access for superadmin/admin (pipeline: "all")
  const userPipeline = user?.pipeline || "all";
  const userRole = user?.role || "user";
  const hasPipelineAccess = userPipeline === "all" || userRole === "superadmin" || userRole === "admin";

  const pipelineItems = hasPipelineAccess ? [
    { type: "separator", label: "Pipelines" },
    { path: "/v1/dashboard", icon: FileText, label: "XOrbit / VJA", color: "#8b5cf6" },
    { path: "/v1/cocb/dashboard", icon: FileText, label: "COCB", color: "#f59e0b" },
    { path: "/gfs/dashboard", icon: FileText, label: "GFS", color: "#10b981" },
  ] : [];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    onLogout();
    navigate("/login");
  };

  return (
    <div className="main-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-text">KFintech OCR</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`nav-item ${isActive(item.path) ? "active" : ""} ${item.external ? "nav-item-external" : ""}`}
              onClick={() => {
                if (item.external) {
                  window.open(item.path, "_blank");
                } else {
                  navigate(item.path);
                }
              }}
              title={item.label}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
          {pipelineItems.map((item) => (
            item.type === "separator" ? (
              <div key={item.label} className="nav-separator" style={{padding: "12px 16px 4px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#94a3b8", marginTop: 8}}>{item.label}</div>
            ) : (
              <button
                key={item.path}
                className={`nav-item ${location.pathname.startsWith(item.path.split("/dashboard")[0]) ? "active" : ""}`}
                onClick={() => navigate(item.path)}
                title={item.label}
                style={{borderLeft: `3px solid ${item.color || "transparent"}`}}
              >
                <item.icon size={20} style={{color: item.color}} />
                <span>{item.label}</span>
              </button>
            )
          ))}
        </nav>

        <div className="sidebar-footer">
          {/* <button
            className={`nav-item ${isActive("/settings") ? "active" : ""}`}
            onClick={() => navigate("/settings")}
            title="Settings"
          >
            <Settings size={20} />
            <span>Settings</span>
          </button> */}
          <button
            className="nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="layout-content">
        {/* Top Header */}
        <header className="top-header">
          <div className="header-title">
            {location.pathname === '/create-work-order' 
              ? 'Create Work Order'
              : location.pathname.startsWith('/work-order/') 
                ? 'Work Order Details'
                : menuItems.find((item) => isActive(item.path))?.label || "Dashboard"}
          </div>

          <div className="header-actions">
            <NotificationsPanel />

            <div className="user-menu">
              <button
                className="user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <span className="user-name">{user?.name}</span>
              </button>

              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="dropdown-name">{user?.name}</div>
                      <div className="dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default MainLayout;
